/**
 * Shared Role Form Component - Redesigned
 * Slide-over panel for creating/editing roles with improved UX and pre-populated permissions
 */

import { useState, useEffect, useCallback, type JSX } from 'react'
import type { Role, Permission, DialogMode, CreateRoleInput, UpdateRoleInput } from './types.js'
import { withoutPermissionConflicts } from '../../utils/permissionConstraints.js'

interface RoleFormProps {
  visible: boolean
  mode: DialogMode
  role: Role | null
  permissions: Record<string, Permission[]>
  permissionOptions: (Permission & { domain?: string })[]
  loading: boolean
  onSave: (data: CreateRoleInput | UpdateRoleInput) => Promise<void>
  onCancel: () => void
}

export function RoleForm({
  visible,
  mode,
  role,
  permissions,
  permissionOptions,
  loading,
  onSave,
  onCancel,
}: RoleFormProps): JSX.Element {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionKeys: [] as string[],
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedDomains, setExpandedDomains] = useState<Record<string, boolean>>({})
  const [nameError, setNameError] = useState('')
  const [descriptionError, setDescriptionError] = useState('')

  useEffect(() => {
    if (!visible) return
    if (mode === 'edit' && role) {
      setFormData({
        name: role.name,
        description: role.description || '',
        permissionKeys: role.permissions || role.permissionKeys || [],
      })
    } else if (mode === 'create') {
      setFormData({ name: '', description: '', permissionKeys: [] })
      setSearchQuery('')
    }
    setNameError('')
    setDescriptionError('')
    // Expand all domains by default
    const domains = Object.keys(permissions)
    const expanded: Record<string, boolean> = {}
    for (const d of domains) expanded[d] = true
    setExpandedDomains(expanded)
  }, [visible, mode, role, permissions])

  // Build grouped structure from permissionOptions or the permissions prop
  const grouped = Object.entries(permissions)

  const filteredGrouped = grouped.map(([domain, perms]) => ({
    domain,
    perms: searchQuery
      ? perms.filter(
          (p) =>
            p.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.description || '').toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : perms,
  })).filter((g) => g.perms.length > 0)

  const allPermissionKeys = permissionOptions.map((p) => p.key)

  // Keys the bulk "Select All" grants. It must yield a set the backend will
  // actually save, otherwise "Select All → Save" 400s and looks broken:
  //  - drop the '*' wildcard sentinel — "select every granular permission"
  //    should never silently grant omnipotent access, and
  //  - drop sox:approve when billing:manage is also present, because SOX
  //    segregation of duties forbids a single role holding both
  //    (enforceSoxSegregation in create-role.ts / update-role.ts).
  // Both remain individually selectable in the tree below; only the bulk
  // action skips them.
  const bulkSelectableKeys = withoutPermissionConflicts(
    allPermissionKeys.filter((k) => k !== '*'),
  )
  const isAllSelected =
    bulkSelectableKeys.length > 0 &&
    bulkSelectableKeys.every((k) => formData.permissionKeys.includes(k))

  const togglePermission = useCallback((key: string) => {
    setFormData((prev) => ({
      ...prev,
      permissionKeys: prev.permissionKeys.includes(key)
        ? prev.permissionKeys.filter((k) => k !== key)
        : [...prev.permissionKeys, key],
    }))
  }, [])

  const toggleDomain = useCallback((domain: string, perms: Permission[]) => {
    const domainKeys = perms.map((p) => p.key)
    const allSelected = domainKeys.every((k) => formData.permissionKeys.includes(k))
    setFormData((prev) => ({
      ...prev,
      permissionKeys: allSelected
        ? prev.permissionKeys.filter((k) => !domainKeys.includes(k))
        : [...new Set([...prev.permissionKeys, ...domainKeys])],
    }))
  }, [formData.permissionKeys])

  const handleSelectAll = () => {
    setFormData((prev) => ({ ...prev, permissionKeys: isAllSelected ? [] : [...bulkSelectableKeys] }))
  }

  const validateRoleName = (name: string): string => {
    if (!name.trim()) {
      return 'Role name is required'
    }
    if (name.length > 100) {
      return 'Role name must be at most 100 characters'
    }
    const nameRegex = /^[a-zA-Z0-9_\s-]+$/
    if (!nameRegex.test(name)) {
      return 'Role name can only contain letters, numbers, spaces, underscores, and hyphens. Examples: "Admin", "Sales Manager", "content_editor", "super-admin", "support_manager"'
    }
    return ''
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value
    setFormData({ ...formData, name: newName })
    setNameError(validateRoleName(newName))
  }

  const validateDescription = (description: string): string => {
    if (!description.trim()) {
      return 'Description is required'
    }
    return ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const nameErr = validateRoleName(formData.name)
    const descErr = validateDescription(formData.description)
    if (nameErr) setNameError(nameErr)
    if (descErr) setDescriptionError(descErr)
    if (nameErr || descErr) return
    await onSave({
      name: formData.name,
      description: formData.description,
      permissionKeys: formData.permissionKeys,
    })
  }

  const isView = mode === 'view'
  const isEdit = mode === 'edit'
  const title = isView ? 'View Role' : isEdit ? 'Edit Role' : 'Create Role'

  return (
    <>
      {visible && (
        <div className="fixed inset-0 bg-black/40 z-[9998]" onClick={onCancel} />
      )}
      <div
        className={`fixed top-0 right-0 h-full w-[540px] bg-white shadow-2xl z-[9999] flex flex-col transform transition-transform duration-300 ease-in-out ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
              <i className="pi pi-shield text-white text-sm" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{title}</h2>
              {role && isEdit && (
                <p className="text-xs text-gray-400">{formData.permissionKeys.length} permission{formData.permissionKeys.length !== 1 ? 's' : ''} selected</p>
              )}
            </div>
          </div>
          <button onClick={onCancel} className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all">
            <i className="pi pi-times" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Role Identity */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-50">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Role Identity</h3>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-700">Role Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={handleNameChange}
                    disabled={isView}
                    required
                    placeholder="e.g. SUPPORT_MANAGER"
                    className={`w-full rounded-xl border px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400 ${
                      nameError ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 bg-gray-50/50 focus:border-[#f6931c] focus:bg-white focus:ring-2 focus:ring-[#f6931c]/10'
                    }`}
                  />
                  {nameError && <p className="text-xs text-red-500">{nameError}</p>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-700">Description <span className="text-red-500">*</span></label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value })
                      if (descriptionError) setDescriptionError(validateDescription(e.target.value))
                    }}
                    onBlur={() => setDescriptionError(validateDescription(formData.description))}
                    disabled={isView}
                    rows={3}
                    placeholder="Brief description..."
                    className={`w-full rounded-xl border px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all resize-none disabled:bg-gray-50 disabled:text-gray-400 ${
                      descriptionError ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100' : 'border-gray-200 bg-gray-50/50 focus:border-[#f6931c] focus:bg-white focus:ring-2 focus:ring-[#f6931c]/10'
                    }`}
                  />
                  {descriptionError && <p className="text-xs text-red-500">{descriptionError}</p>}
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-50 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Permissions</h3>
                {!isView && (
                  <button type="button" onClick={handleSelectAll} className="text-xs font-semibold text-[#3454cf] hover:text-[#2a45b0]">
                    {isAllSelected ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
              <div className="p-5 space-y-4">
                {/* Search */}
                {!isView && (
                  <div className="relative">
                    <i className="pi pi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search permissions..."
                      className="w-full h-10 pl-9 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 outline-none transition-all"
                    />
                  </div>
                )}

                {/* Selected badges */}
                {formData.permissionKeys.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 p-3 bg-[#3454cf]/5 rounded-sm border border-[#3454cf]/10">
                    {formData.permissionKeys.slice(0, 8).map((key) => (
                      <span key={key} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#3454cf]/10 text-[#3454cf] text-[10px] rounded-sm font-mono font-semibold">
                        {key}
                        {!isView && (
                          <button type="button" onClick={() => togglePermission(key)} className="text-[#3454cf]/60 hover:text-[#3454cf]">×</button>
                        )}
                      </span>
                    ))}
                    {formData.permissionKeys.length > 8 && (
                      <span className="text-[10px] text-[#3454cf]/70 self-center">+{formData.permissionKeys.length - 8} more</span>
                    )}
                  </div>
                )}

                {/* Grouped permission tree */}
                <div className="space-y-2 max-h-72 overflow-y-auto border border-gray-100 rounded-sm">
                  {filteredGrouped.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-6">No permissions found</p>
                  )}
                  {filteredGrouped.map(({ domain, perms }) => {
                    const domainKeys = perms.map((p) => p.key)
                    const domainSelected = domainKeys.filter((k) => formData.permissionKeys.includes(k))
                    const allDomainSelected = domainKeys.every((k) => formData.permissionKeys.includes(k))
                    const isExpanded = expandedDomains[domain] ?? true
                    return (
                      <div key={domain} className="border-b border-gray-50 last:border-0">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50/50 hover:bg-gray-50 transition-colors text-left"
                          onClick={() => setExpandedDomains((prev) => ({ ...prev, [domain]: !prev[domain] }))}
                        >
                          <div className="flex items-center gap-2">
                            {!isView && (
                              <input
                                type="checkbox"
                                checked={allDomainSelected}
                                onChange={(e) => { e.stopPropagation(); toggleDomain(domain, perms) }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                              />
                            )}
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{domain}</span>
                            <span className="text-[10px] text-gray-400 bg-white border border-gray-100 rounded-sm px-1.5 py-0.5">{domainSelected.length}/{perms.length}</span>
                          </div>
                          <i className={`pi pi-chevron-down text-[10px] text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        {isExpanded && (
                          <div className="divide-y divide-gray-50">
                            {perms.map((perm) => {
                              const isChecked = formData.permissionKeys.includes(perm.key)
                              return (
                                <label
                                  key={perm.key}
                                  className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer transition-colors ${isChecked ? 'bg-[#3454cf]/5' : 'hover:bg-gray-50'} ${isView ? 'cursor-default' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => !isView && togglePermission(perm.key)}
                                    disabled={isView}
                                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <span className="block text-xs font-mono text-gray-800 truncate">{perm.key}</span>
                                    {perm.description && <span className="block text-[10px] text-gray-400 mt-0.5 truncate">{perm.description}</span>}
                                  </div>
                                </label>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          {!isView && (
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-white shrink-0">
              <button
                type="button"
                onClick={onCancel}
                className="text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="text-sm font-semibold px-5 py-2.5 rounded-sm bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 text-white transition-all flex items-center gap-2"
              >
                {loading && <i className="pi pi-spin pi-spinner text-xs" />}
                {isEdit ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          )}
        </form>
      </div>
    </>
  )
}
